import IconCopy from '@apollo/icons/default/IconCopy.svg';
import IconRun from '@apollo/icons/default/IconRun.svg';
import { ButtonGroup, IconButton, Code as OrbitCode } from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import type { DocumentNode } from 'graphql';
import { parse, print } from 'graphql';
import _ from 'lodash';
import React, { useEffect, useReducer } from 'react';
import { Link } from 'react-router-dom';
import Prism from 'react-syntax-highlighter/dist/esm/prism-light';
import graphql from 'refractor/graphql';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { prismStudioThemeLight } from 'src/components/graphqlCodeBlock/prismStudioThemeLight';
import { PreTag } from 'src/components/PreTag';
import { TooltipWithMutableContent } from 'src/components/TooltipWithMutableContent';

import type { EventCategory } from '../analytics';

Prism.registerLanguage('graphql', graphql);

// Less common languages live in a separate chunk that is fetched on first
// markdown render. Until it loads, those blocks render as plain text.
let extraLanguagesPromise: Promise<unknown> | null = null;
// Returns a counter that increments once the extras chunk has registered its
// languages. Used as a `key` on the <Prism> element so the React Compiler is
// forced to re-create it — without that, Prism's render is memoized against
// unchanged props and never re-runs `refractor.highlight` to pick up the
// newly registered grammars.
const useExtraLanguages = () => {
  const [version, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!extraLanguagesPromise) {
      // eslint-disable-next-line react-hooks/todo -- dynamic import() not yet lowered by React Compiler
      extraLanguagesPromise = import(
        /* webpackChunkName: "markdown-extra-languages" */
        './markdownExtraLanguages'
      );
    }
    let cancelled = false;
    extraLanguagesPromise.then(() => {
      if (!cancelled) bump();
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return version;
};

const tryParseGraphql = (
  source: string,
):
  | { type: 'result'; result: DocumentNode }
  | { type: 'error'; error: Error } => {
  try {
    const result = parse(source);
    return { result, type: 'result' };
  } catch (error) {
    return {
      error: error as Error,
      type: 'error',
    };
  }
};

export const Code = ({
  className,
  children,
  analyticsCategory,
  analyticsLabelPrefix,
  hideActionButtons = false,
  inline = true,
}: {
  className: string | undefined;
  children: string;
  analyticsCategory?: EventCategory;
  analyticsLabelPrefix?: string;
  hideActionButtons?: boolean;
  inline?: boolean;
}) => {
  const extraLanguagesVersion = useExtraLanguages();
  const language = className?.replace('lang-', '');

  const { locationToExplorer } = useInternalGraphLinking();
  if (inline && !children.includes('\n')) {
    return <OrbitCode>{children}</OrbitCode>;
  }

  const parsedResult = tryParseGraphql(children);
  const targetLanguage = language === 'gql' ? 'graphql' : language;

  return (
    <div className="my-4 flex rounded border border-primary bg-secondary px-4 py-3">
      <PreTag.DefineProps
        value={({ style, ...props }) => ({
          ...props,
          className: classnames('w-full text-base', {
            'py-1': !hideActionButtons,
          }),
          style: {
            ..._.omit(
              style,
              'fontSize',
              'fontFamily',
              'margin',
              'padding',
              'background',
            ),
          },
        })}
      >
        <Prism
          key={extraLanguagesVersion}
          style={prismStudioThemeLight}
          language={targetLanguage}
          PreTag={PreTag}
          codeTagProps={{
            className: `w-full rounded ${language ? `language-${targetLanguage}` : ''}`,
            style: {
              ..._.omit(
                {
                  ...prismStudioThemeLight['code[class*="language-"]'],
                  ...prismStudioThemeLight[
                    `code[class*="language-${targetLanguage}"]`
                  ],
                },
                'fontFamily',
              ),
              background: 'none',
              whiteSpace: 'pre-wrap',
            },
          }}
        >
          {children}
        </Prism>
      </PreTag.DefineProps>
      {!hideActionButtons &&
        language &&
        ['graphql', 'gql'].includes(language) && (
          <ButtonGroup className="self-start">
            <TooltipWithMutableContent
              initialContent="Copy codeblock"
              placement="top"
            >
              {({ setContent, resetContent }) => (
                <IconButton
                  variant="hidden"
                  size="sm"
                  onClick={() => {
                    copy(children);
                    setContent('Copied');
                  }}
                  onMouseEnter={() => resetContent()}
                  data-analytics-label={
                    analyticsLabelPrefix
                      ? `${analyticsLabelPrefix}: Copy codeblock`
                      : undefined
                  }
                  data-analytics-category={analyticsCategory}
                  aria-label="Copy codeblock"
                  icon={<IconCopy />}
                />
              )}
            </TooltipWithMutableContent>
            <Tooltip
              label={
                parsedResult.type === 'result'
                  ? 'Open in Explorer'
                  : 'Invalid graphql - cannot open in Explorer.'
              }
              placement="top"
            >
              {parsedResult.type === 'error' ? (
                <IconButton
                  size="sm"
                  icon={<IconRun />}
                  isDisabled={true}
                  aria-label="Open in Explorer"
                />
              ) : (
                <IconButton
                  size="sm"
                  icon={<IconRun />}
                  as={Link}
                  to={locationToExplorer({
                    document: print(parsedResult.result),
                  })}
                  data-analytics-label={
                    analyticsLabelPrefix
                      ? `${analyticsLabelPrefix}: Open in Explorer`
                      : undefined
                  }
                  data-analytics-category={analyticsCategory}
                  aria-label="Open in Explorer"
                />
              )}
            </Tooltip>
          </ButtonGroup>
        )}
    </div>
  );
};
